import React, {useEffect} from 'react';

import range from 'lodash/range';
import {Link, withRouter, WithRouterProps} from 'react-router';

import type {Location} from 'history';
import type {ListResponseType} from 'sweet-types';

const DEFAULT_PAGE_NUM = '1';

export interface Props<T> {
  page?: string | number;
  className?: string;
  pageParamName?: string;
  router: WithRouterProps['router'];
  location: WithRouterProps['location'];
  isLoading: boolean;
  response: ListResponseType<T>;
}

export const LinkPaginationRTK = withRouter(function <T>({
  response,
  className,
  isLoading,
  router,
  location,
  pageParamName = 'page',
  page = DEFAULT_PAGE_NUM,
}: Props<T>): JSX.Element | null {
  useEffect(() => {
    if (page > response.num_pages) {
      router.replace({
        pathname: location.pathname,
        query: {
          ...location.query,
          [pageParamName]: response.num_pages,
        },
      });
    }
  }, [response]);

  if (isLoading === true || response.num_pages < 2) {
    return null;
  }

  const {num_pages, next, previous} = response;

  const links = range(1, num_pages + 1).map((pageIndex) => ({
    active: pageIndex.toString() === page,
    page: pageIndex.toString(),
  }));

  return (
    <div
      className={`d-flex justify-content-center ${
        className != null ? className : ''
      }`}
    >
      <ul className="pagination mx-auto flex-wrap justify-content-center">
        {previous != null ? (
          <li key={'previous'} className="page-item">
            <Link
              className="page-link"
              to={createPaginationClickHandler(Number(page) - 1, pageParamName)}
            >
              <i className="fa fa-arrow-left" />
            </Link>
          </li>
        ) : null}

        {links.map((link) => (
          <li
            key={link.page}
            className={`page-item ${link.active ? 'active' : ''}`}
          >
            <Link
              className="page-link"
              to={createPaginationClickHandler(link.page, pageParamName)}
            >
              {link.page}
            </Link>
          </li>
        ))}

        {next != null ? (
          <li key={'next'} className="page-item">
            <Link
              className="page-link"
              to={createPaginationClickHandler(Number(page) + 1, pageParamName)}
            >
              <i className="fa fa-arrow-right" />
            </Link>
          </li>
        ) : null}
      </ul>
    </div>
  );
});

const createPaginationClickHandler =
  (pageIndex: number | string, pageParamName: string) =>
  (location: Location) => ({
    pathname: location.pathname,
    query: {
      ...location.query,
      [pageParamName]: pageIndex,
    },
  });
